import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import CursorTrail from './CursorTrail';
import { usePublishedContent } from '@/context/ContentContext';
import { EasterEggSettings, CursorTrailStyle } from '@/types/content';

interface EasterEggContextType {
  triggerLogoClick: () => void;
  logoClickCount: number;
  cursorTrailActive: boolean;
  setCursorTrailActive: (active: boolean) => void;
  godModeUnlocked: boolean;
}

const EasterEggContext = createContext<EasterEggContextType | undefined>(undefined);

const defaultEasterEggs: EasterEggSettings = {
  logo_click: {
    enabled: false,
    clicks_required: 5,
    effect: 'confetti',
  },
  cursor_trail: {
    enabled: false,
    style: 'stars',
    trigger: 'after_logo_click',
  },
};

export const EasterEggProvider = ({ children }: { children: ReactNode }) => {
  const content = usePublishedContent();
  const settings = content.site_settings?.easter_eggs || defaultEasterEggs;
  
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [showSecretMessage, setShowSecretMessage] = useState(false);
  const [godModeUnlocked, setGodModeUnlocked] = useState(() => {
    return localStorage.getItem('easter_egg_god_mode_unlocked') === 'true';
  });
  const [cursorTrailActive, setCursorTrailActive] = useState(
    settings.cursor_trail.enabled && settings.cursor_trail.trigger === 'always'
  );

  const triggerEffect = useCallback((effect: string, message?: string) => {
    switch (effect) {
      case 'confetti':
        // Fire confetti from both sides
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { x: 0.1, y: 0.5 },
          colors: ['#FF69B4', '#00D4FF', '#FFD700', '#9B59B6'],
        });
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { x: 0.9, y: 0.5 },
          colors: ['#FF69B4', '#00D4FF', '#FFD700', '#9B59B6'],
        });
        break;
        
      case 'fireworks':
        const duration = 2000;
        const animationEnd = Date.now() + duration;
        const interval = setInterval(() => {
          if (Date.now() > animationEnd) {
            clearInterval(interval);
            return;
          }
          confetti({
            particleCount: 50,
            startVelocity: 30,
            spread: 360,
            origin: {
              x: Math.random(),
              y: Math.random() * 0.5,
            },
            colors: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF'],
          });
        }, 250);
        break;
        
      case 'secret_message':
        setShowSecretMessage(true);
        setTimeout(() => setShowSecretMessage(false), 4000);
        break;
        
      case 'spin_page':
        document.body.style.transition = 'transform 1s ease-in-out';
        document.body.style.transform = 'rotate(360deg)';
        setTimeout(() => {
          document.body.style.transform = 'rotate(0deg)';
          setTimeout(() => {
            document.body.style.transition = '';
          }, 1000);
        }, 1000);
        break;
    }
  }, []);

  const triggerLogoClick = useCallback(() => {
    if (!settings.logo_click.enabled) return;
    
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount >= settings.logo_click.clicks_required) {
      // Trigger effect
      triggerEffect(settings.logo_click.effect, settings.logo_click.secret_message);
      
      // Unlock God Mode
      setGodModeUnlocked(true);
      localStorage.setItem('easter_egg_god_mode_unlocked', 'true');
      
      // Activate cursor trail if trigger is 'after_logo_click'
      if (settings.cursor_trail.enabled && settings.cursor_trail.trigger === 'after_logo_click') {
        setCursorTrailActive(true);
      }
      
      // Reset counter
      setLogoClickCount(0);
    }
  }, [logoClickCount, settings, triggerEffect]);

  const secretMessage = settings.logo_click.secret_message || '🎉 Du hast das Easter Egg gefunden! 🎉';

  return (
    <EasterEggContext.Provider value={{ 
      triggerLogoClick, 
      logoClickCount,
      cursorTrailActive,
      setCursorTrailActive,
      godModeUnlocked,
    }}>
      {children}
      
      {/* Cursor Trail */}
      <CursorTrail 
        style={settings.cursor_trail.style} 
        enabled={settings.cursor_trail.enabled && cursorTrailActive} 
      />
      
      {/* Secret Message Overlay */}
      <AnimatePresence>
        {showSecretMessage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          >
            <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white px-8 py-6 rounded-2xl shadow-2xl text-2xl font-bold animate-pulse">
              {secretMessage}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </EasterEggContext.Provider>
  );
};

export const useEasterEgg = () => {
  const context = useContext(EasterEggContext);
  if (!context) {
    throw new Error('useEasterEgg must be used within an EasterEggProvider');
  }
  return context;
};